import Raven from 'raven-js';
import { useCallback, useMemo, useState } from 'react';
import CMPDeleteModal from 'content-management-actions-lib/modals/DeleteModal';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue, getPropertyValue } from 'content-management-data-lib';
import { CATEGORY_IDS } from 'ContentUtils/constants/Categories';
import Routes from 'ContentUtils/Routes';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { View } from 'foundations-components/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { MatchTextArea } from 'foundations-components/transitional/components/MatchTextArea';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { PAGE_PROPERTY_NAMES } from '../../constants/propertyNames';
import { useFetchPersonalizationVariants } from '../../hooks/useFetchPersonalizationVariants';
import { useDeletePersonalizedContent } from '../../hooks/useDeletePersonalizedContent';
import { isPersonalizationDefault, isContentObjectPersonalized } from '../../utils/personalization';
import { useContentActions } from 'content-management-actions-lib/providers/ContentActionsProvider';
import { CONTENT_ACTION_IDS } from 'content-management-actions-lib/constants/actionIds';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const displayDeleteSuccessMessage = () => {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-lib.actions.successes.message.deletePersonalizationVariant"
    }),
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-lib.actions.successes.title"
    }),
    type: 'success'
  });
};
const displayDeleteErrorMessage = () => {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-lib.actions.errors.message"
    }),
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-lib.actions.errors.title.deletePersonalizationVariant"
    }),
    type: 'danger'
  });
};
const Header = ({
  isDeletingDefault
}) => isDeletingDefault ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
  message: "page-lib.personalization.deleteActionOverrideModal.header.personalizationDefault"
}) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
  message: "page-lib.personalization.deleteActionOverrideModal.header.personalizationVariant"
});
const ModalBody = ({
  isDeletingDefault,
  personalizationVariants
}) => {
  if (isDeletingDefault) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "page-lib.personalization.deleteActionOverrideModal.body.personalizationDefault"
      }), /*#__PURE__*/_jsx(List, {
        styled: true,
        children: personalizationVariants.map(variant => {
          if (variant.personalizationVariantStatus === 'DEFAULT') {
            return null;
          }
          return /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Link, {
              external: true,
              href: Routes.details(variant.id, CATEGORY_IDS.LANDING_PAGE),
              children: variant.name
            })
          }, variant.id);
        })
      })]
    });
  }
  const personalizationDefaultPage = personalizationVariants.find(variantPage => variantPage.personalizationVariantStatus === 'DEFAULT');
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "page-lib.personalization.deleteActionOverrideModal.body.personalizationVariant_jsx",
    elements: {
      Link,
      strong: ({
        children
      }) => /*#__PURE__*/_jsx(Text, {
        as: "strong",
        children: children
      })
    },
    options: {
      linkProps: {
        external: true,
        href: Routes.details(personalizationDefaultPage.id, CATEGORY_IDS.LANDING_PAGE)
      },
      pageName: personalizationDefaultPage.name
    }
  });
};
const DeletePersonalizedContentModal = ({
  crmObjects,
  onClose
}) => {
  const [matched, setMatched] = useState(false);
  const {
    onActionSuccess
  } = useContentActions();
  // NOTE: bulk deletion of personalized content is currently disabled, so we'll only ever have one crmObject here.
  const targetCrmObject = crmObjects[0];
  const targetContentId = getPropertyValue(targetCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.ID);
  const personalizationStrategyId = getContentPropertyValue(targetCrmObject, PAGE_PROPERTY_NAMES.PERSONALIZATION_STRATEGY_ID);
  const isDeletingDefault = isPersonalizationDefault(targetCrmObject);
  const {
    personalizationVariants,
    loading: isFetchingVariantsLoading,
    error: __error
  } = useFetchPersonalizationVariants(targetContentId);
  const {
    deletePersonalizedContent,
    deleteIsLoading,
    deleteError,
    isDeleteReady
  } = useDeletePersonalizedContent({
    contentId: targetContentId,
    personalizationStrategyId,
    isDeletingDefault
  });
  const isDeleteDisabled = useMemo(() => {
    return !matched || isFetchingVariantsLoading || !isDeleteReady;
  }, [matched, isFetchingVariantsLoading, isDeleteReady]);
  const onConfirmDelete = useCallback(async () => {
    try {
      await deletePersonalizedContent();
      displayDeleteSuccessMessage();
      onClose();
      onActionSuccess({
        actionId: CONTENT_ACTION_IDS.DELETE,
        data: {
          completedResults: [targetCrmObject],
          actionData: {
            isPersonalizedLandingPageDelete: true,
            isDeletingDefault
          }
        }
      });
    } catch (error) {
      displayDeleteErrorMessage();
      Raven.captureMessage('Delete personalized content failed', {
        extra: {
          error,
          deleteError
        }
      });
    }
  }, [deletePersonalizedContent, onClose, onActionSuccess, targetCrmObject, isDeletingDefault, deleteError]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "danger",
    "data-test-id": "delete-personalized-content-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(Header, {
          isDeletingDefault: isDeletingDefault
        })
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: isFetchingVariantsLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
        justifySelf: "center"
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ModalBody, {
          isDeletingDefault: isDeletingDefault,
          personalizationVariants: personalizationVariants
        }), /*#__PURE__*/_jsxs(View, {
          marginBlockStart: "600",
          children: [/*#__PURE__*/_jsx(FormLabel, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "page-lib.personalization.deleteActionOverrideModal.matchTextConfirm"
            })
          }), /*#__PURE__*/_jsx(MatchTextArea, {
            "data-test-id": "match-text-input",
            match: I18n.text('content-management-actions-lib.modals.delete.deleteMatch.text'),
            onMatchedChange: ({
              target: {
                value
              }
            }) => setMatched(value),
            size: "xxl"
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton
      // allows for treating this modal as a UIConfirmModal in Selenium
      , {
        "data-confirm-button": "accept",
        loading: deleteIsLoading,
        disabled: isDeleteDisabled,
        onClick: onConfirmDelete,
        use: "danger",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "content-management-actions-lib.modals.delete.buttons.delete"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "content-management-actions-lib.modals.delete.buttons.cancel"
        })
      })]
    })]
  });
};
const DeleteLandingPageModal = modalProps => {
  const {
    crmObjects
  } = modalProps;
  const isUsingPersonalization = crmObjects.some(isContentObjectPersonalized);
  if (isUsingPersonalization) {
    return /*#__PURE__*/_jsx(DeletePersonalizedContentModal, Object.assign({}, modalProps));
  }
  return /*#__PURE__*/_jsx(CMPDeleteModal, Object.assign({}, modalProps));
};
export default DeleteLandingPageModal;